/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: external-metadata-queue-component.ts
 *
 * Created by kyang on 2019-08-02.
 */

function ExternalMetadataQueueComponent (
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    $q: JS5Object,
    $timeout: any,
    uiGridConstants: JS5Object,
    ExternalMetadataQueueResourceFactory: JS5Object,
    ModalService: JS5Object,
    NavbarUtilService: JS5Object,
    NotificationService: JS5Object,
    PaginationService: JS5Object,
    RequestService: JS5Object,
    SearchFilterService: JS5Object
) {

    let loadDataTimeout: JS5Promise;

    //======//
    // Grid //
    //======//

    const handleSortColumnChange = (grid: JS5Object, sortColumns: JS5Object[]) => {
        let newColumn = _.last(sortColumns);
        if (newColumn.suppressRemoveSort) {
            getEditData();
            return;
        }
        newColumn.suppressRemoveSort = true;
        newColumn.priority = 0;

        if (newColumn.colDef.field === 'dateCreated') {
            let priorityColumn = _.find(sortColumns, {colDef: {field: 'priority'}});
            priorityColumn.suppressRemoveSort = false;
            priorityColumn.sort.priority = 1;
            priorityColumn.unsort();
            getEditData();
        } else if (newColumn.colDef.field === 'priority') {
            let dateColumn = _.find(sortColumns, {colDef: {field: 'dateCreated'}});
            dateColumn.suppressRemoveSort = false;
            dateColumn.sort.priority = 1;
            dateColumn.unsort();
            getEditData();
        }
    };

    let gridColumnDefs = [
        {
            name: 'checkMarkRowHeaderCol',
            field: 'checkMark',
            width: '2%',
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'components/visual/grid/check-mark-template.html',
            headerCellTemplate: 'components/visual/grid/header-check-mark-component-template.html'
        },
        {
            cellTemplate: 'system/externalMetadata/queue/cellTemplates/external-ingest-priority-cell-template.html',
            displayName: 'Priority',
            field: 'priority',
            sort: {
                direction: 'desc',
            },
            sortDirectionCycle: [null, uiGridConstants.DESC],
            suppressRemoveSort: true,
            type: 'number',
            width: '85',
        }, {
            cellTemplate: 'system/externalMetadata/queue/cellTemplates/external-ingest-source-cell-template.html',
            displayName: 'Source ID',
            enableSorting: false,
            field: 'sourceId',
            type: 'number',
            width: '100',
        }, {
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
            displayName: 'Name',
            enableSorting: false,
            field: 'name',
            width: '*',
        }, {
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
            displayName: 'System Name',
            enableSorting: false,
            field: 'systemName',
            width: '*',
        }, {
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
            displayName: 'Filename',
            enableSorting: false,
            field: 'filename',
            width: '*',
        }, {
            cellFilter: `date:'MM/dd/yyyy hh:mm:ss a'`,
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
            displayName: 'Date Created',
            field: 'dateCreated',
            sort: {
                priority: 1
            },
            sortDirectionCycle: [null, uiGridConstants.ASC],
            type: 'number',
            width: 195,
        }];

    this.gridOptions = {
        enableColumnMenus: false,
        enableColumnResizing: true,
        enableFullRowSelection: true,
        enableRowHeaderSelection: false,
        enableRowSelection: true,
        enableSorting: true,
        enableVerticalScrollbar: uiGridConstants.ALWAYS,
        multiSelect: true,
        columnDefs: gridColumnDefs,
        rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
        useExternalSorting: true,
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
            gridApi.core.on.sortChanged(null, handleSortColumnChange);
        }
    };

    this.onSelectAllToggled = () => {
        if (this.gridApi.selection.getSelectedCount() === this.gridOptions.data.length) {
            this.gridApi.selection.clearSelectedRows();
        } else {
            this.gridApi.selection.selectAllRows();
        }
    };

    //================//
    // Template Logic //
    //================//

    const showExternalIngestQueueEditorModal = () => {
        ModalService.showExternalIngestQueueEditorModal(this.gridApi.selection.getSelectedRows(), onModalSave);
    };

    const cancelExternalIngests = (cancellingIngests: JS5Object[]) => {
        this.loadingIndicator.show = true;
        $timeout.cancel(loadDataTimeout);
        return ExternalMetadataQueueResourceFactory.cancelIngests(
            {jobId: cancellingIngests.map(ingest => ingest.id)}
        ).$promise
            .then(getEditData)
            .catch(getEditData);
    };

    this.toolbarButtonDefs = [
        {
            label: 'Edit',
            enabled: () => this.gridApi && this.gridApi.selection.getSelectedCount() > 0,
            onClick: () => showExternalIngestQueueEditorModal()
        },
        {
            icon: 'fa-times',
            label: 'Cancel',
            enabled: () => this.gridApi && this.gridApi.selection.getSelectedCount() > 0,
            onClick: () => cancelExternalIngests(this.gridApi.selection.getSelectedRows()),
        },
    ];

    const onModalSave = (modifiedExternalIngestJobs: JS5Object[]) => {
        this.loadingIndicator.show = true;
        $q.all(_.map(modifiedExternalIngestJobs, (ingestJob: JS5Object) => {
            return ExternalMetadataQueueResourceFactory.update(ingestJob).$promise
        }))
            .catch(() => {
                NotificationService.createWarningNotification('Failed to set priority on some of the external ingest jobs: Some jobs were already started / cancelled', '', true);
            })
            .finally(getEditData);
    };

    this.onPriorityCellDoubleClicked = (row: JS5Object, event: JS5Object) => {
        event.preventDefault();
        event.stopPropagation();
        row.setSelected(true);
        showExternalIngestQueueEditorModal();
    };

    this.onIngestCellDoubleClicked = (row: JS5Object, event: JS5Object) => {
        $timeout.cancel(loadDataTimeout);
        event.preventDefault();
        event.stopPropagation();
        this.loadingIndicator.show = true;
        $location.url(`/system/aggregation/edit/${row.entity.sourceId}/`);
    };

    NavbarUtilService.viewSearchFunction = (searchText: JS5Object, forceRefresh: false) => {
        getEditData();
    };


    //============//
    // Pagination //
    //============//

    const paginationPrevPageHandler = () => {
        --this.pagingOptions.currentPage;
        getEditData();
    };

    const paginationNextPageHandler = () => {
        ++this.pagingOptions.currentPage;
        getEditData();
    };

    const paginationPageSizeChanged = () => {
        this.pagingOptions.currentPage = 1;
        getEditData();
    };

    const onPaginationChanged = () => {
        getEditData();
    };

    this.paginationDefs = {
        paginationPrevPageHandler: paginationPrevPageHandler,
        paginationNextPageHandler: paginationNextPageHandler,
        paginationPageSizeChanged: paginationPageSizeChanged,
        paginationJumpToPageHandler: onPaginationChanged,
        pagingOptions: PaginationService,
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    const updatePagination = (externalIngestJobs: JS5Object, responseHeaders: JS5Function) => {
        PaginationService.parseResponseHeaders(responseHeaders());
        PaginationService.pageData = externalIngestJobs;
    };

    const isOrderedByDate = () => {
        let dateCreatedColumn = _.chain(this)
            .get('gridApi.grid.columns', [])
            .find({field: 'dateCreated'})
            .value();
        if (!dateCreatedColumn) {
            return false;
        }
        return !!_.size(dateCreatedColumn.sort);
    };

    const isAscendingSort = () => {
        let columns = _.get(this, 'gridApi.grid.columns');
        if (!columns) {
            return false;
        }
        return _.some(columns, (column: JS5Object) => {
            return _.get(column, 'sort.direction') === 'asc'
        });
    };

    const getDataQuery = () => {
        let criteria = {
            fetchOffset: this.pagingOptions.pageSize * (this.pagingOptions.currentPage - 1),
            fetchLimit: this.pagingOptions.pageSize,
            orderByDate: isOrderedByDate(),
            sortAscending: isAscendingSort(),
            searchString: $location.search().searchText || null,
        };
        return ExternalMetadataQueueResourceFactory.query(criteria, updatePagination).$promise;
    };

    const getEditData = () => {
        this.loadingIndicator.show = true;
        $timeout.cancel(loadDataTimeout);

        let selectedIds: number[] = [];
        if (this.gridApi) {
            selectedIds = this.gridApi.selection.getSelectedRows().map((row: JS5Object) => row.id);
        }
        return RequestService.queryForNewData(getDataQuery)
            .then((externalsQueue: JS5Object[]) => {
                this.gridOptions.data = externalsQueue;
            }).then(() => {
                let deferred = $q.defer();
                $timeout(() => {
                    this.gridApi.selection.clearSelectedRows();
                    _.forEach(selectedIds, (selectedId: JS5Object) => {
                        let matchingRow = _.find(this.gridOptions.data, { id: selectedId });
                        if (matchingRow) {
                            this.gridApi.selection.selectRow(matchingRow);
                        }
                    });

                    deferred.resolve(this.gridOptions.data);
                });
                return deferred;
            }).finally(() => {
                this.loadingIndicator.show = false;
                loadDataTimeout = $timeout(getEditData, 15000);
            });
    };

    this.$onInit = () => {
        loadDataTimeout = null;
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        SearchFilterService.populateCriteriaFromLocationSearch();
        PaginationService.resetToDefaultValues();
        this.pagingOptions = PaginationService;
        getEditData();
    };

    this.$onDestroy = () => {
        $timeout.cancel(loadDataTimeout);
        loadDataTimeout = null;
    };

}

angular.module('prism-ui.externalMetadata').component(
    'externalMetadataQueue',
    {
        controller: ExternalMetadataQueueComponent,
        templateUrl: 'system/externalMetadata/queue/external-metadata-queue-template.html'
    });
